/**
 * Sync Strategy Scheduler
 * 
 * Manages different sync strategy implementations including immediate, delayed,
 * and periodic scheduling with priority-based logic.
 */

import { BaseScheduler, SyncScheduleConfig, SyncPriority, ScheduledSync } from './BaseScheduler';

/**
 * Sync Strategy Scheduler
 * 
 * Handles priority-based scheduling strategies and manages different sync types
 * including immediate, delayed, and periodic synchronization.
 */
export class SyncStrategyScheduler extends BaseScheduler {
  // Scheduling state
  protected currentInterval: number;
  protected nextSyncTime: string | null = null;
  protected scheduledSyncs: Map<string, ScheduledSync> = new Map();
  protected scheduleTimer: NodeJS.Timeout | null = null;

  constructor(config: Partial<SyncScheduleConfig> = {}) {
    super(config);
    this.currentInterval = this.config.defaultInterval;
  }

  /**
   * Subclass-specific initialization
   */
  protected async initializeSubclass(): Promise<void> {
    // Start scheduling if enabled
    if (this.config.enableScheduling) {
      this.startScheduling();
    }
  }

  /**
   * Handle battery level changes
   */
  protected onBatteryLevelChanged(_level: number): void {
    // Recalculate schedule if battery optimization is enabled
    if (this.config.respectBatteryLevel) {
      this.scheduleNextSync('normal', 'battery_level_changed');
    }
  }

  /**
   * Handle configuration updates
   */
  protected onConfigUpdated(updates: Partial<SyncScheduleConfig>): void {
    // Reschedule if scheduling settings changed
    if (updates.enableScheduling !== undefined || 
        updates.defaultInterval !== undefined ||
        updates.enableAdaptiveScheduling !== undefined) {
      this.scheduleNextSync('normal', 'config_updated');
    }
  }

  /**
   * Start scheduling
   */
  private startScheduling(): void {
    this.scheduleNextSync('normal', 'initial_schedule');
  }

  /**
   * Schedule next sync
   */
  scheduleNextSync(priority: SyncPriority = 'normal', reason: string = 'periodic'): void {
    if (!this.config.enableScheduling) return;

    // Calculate next sync interval
    const interval = this.calculateOptimalInterval(priority);
    const nextTime = new Date(Date.now() + interval);
    
    this.nextSyncTime = nextTime.toISOString();

    // Clear existing timer
    if (this.scheduleTimer) {
      clearTimeout(this.scheduleTimer);
    }

    // Schedule the sync
    this.scheduleTimer = setTimeout(() => {
      this.executePendingSyncs();
    }, interval);

    this.emitEvent({
      type: 'sync_scheduled',
      data: {
        nextSyncTime: this.nextSyncTime,
        priority,
        reason
      }
    });

    console.log(`📅 Next sync scheduled for ${nextTime.toLocaleString()} (${interval}ms, ${priority} priority)`);
  }

  /**
   * Calculate optimal sync interval based on priority and conditions
   */
  protected calculateOptimalInterval(priority: SyncPriority): number {
    let baseInterval: number;

    // Get base interval based on priority
    if (this.config.enablePrioritySync) {
      switch (priority) {
        case 'high':
          baseInterval = this.config.highPriorityInterval;
          break;
        case 'low':
          baseInterval = this.config.lowPriorityInterval;
          break;
        default:
          baseInterval = this.config.normalPriorityInterval;
      }
    } else {
      baseInterval = this.config.defaultInterval;
    }

    // Apply battery optimization
    if (this.config.respectBatteryLevel) {
      baseInterval = this.applyBatteryOptimization(baseInterval);
    }

    // Apply quiet hours
    if (this.config.enableQuietHours && this.isQuietHours()) {
      baseInterval = Math.max(baseInterval, this.config.quietHoursInterval);
    }

    // Apply adaptive scheduling
    if (this.config.enableAdaptiveScheduling) {
      baseInterval = this.applyAdaptiveScheduling(baseInterval);
    }

    // Ensure interval is within bounds
    this.currentInterval = Math.max(
      this.config.minInterval,
      Math.min(this.config.maxInterval, baseInterval)
    );

    return this.currentInterval;
  }

  /**
   * Apply battery optimization to interval
   */
  private applyBatteryOptimization(baseInterval: number): number {
    if (this.batteryLevel <= this.config.criticalBatteryThreshold) {
      // Critical battery: use maximum interval
      this.emitEvent({
        type: 'battery_optimization_activated',
        data: {
          batteryLevel: this.batteryLevel,
          newInterval: this.config.maxInterval
        }
      });
      return this.config.maxInterval;
    } else if (this.batteryLevel <= this.config.lowBatteryThreshold) {
      // Low battery: use optimized interval
      this.emitEvent({
        type: 'battery_optimization_activated',
        data: {
          batteryLevel: this.batteryLevel,
          newInterval: this.config.batteryOptimizedInterval
        }
      });
      return Math.max(baseInterval, this.config.batteryOptimizedInterval);
    }

    return baseInterval;
  }

  /**
   * Check if current time is within quiet hours
   */
  protected isQuietHours(): boolean {
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    
    const start = this.config.quietHoursStart;
    const end = this.config.quietHoursEnd;
    
    // Handle overnight quiet hours (e.g., 22:00 to 07:00)
    if (start > end) {
      return currentTime >= start || currentTime <= end;
    }
    
    // Handle same-day quiet hours (e.g., 13:00 to 14:00)
    return currentTime >= start && currentTime <= end;
  }

  /**
   * Apply adaptive scheduling based on usage patterns
   */
  private applyAdaptiveScheduling(baseInterval: number): number {
    if (!this.config.adaptToUsagePatterns) return baseInterval;

    const currentHour = new Date().getHours();
    const hourlyActivity = this.usagePattern.hourlyActivity[currentHour] || 0;
    
    // Increase frequency during high activity periods
    if (hourlyActivity > 0.7) {
      return Math.max(this.config.minInterval, baseInterval * 0.5);
    }
    
    // Decrease frequency during low activity periods
    if (hourlyActivity < 0.3) {
      return Math.min(this.config.maxAdaptiveInterval, baseInterval * 2);
    }
    
    return baseInterval;
  }

  /**
   * Execute pending syncs (to be implemented by BackgroundTaskScheduler)
   */
  protected async executePendingSyncs(): Promise<void> {
    console.log('📅 Executing scheduled sync...');
    // This will be implemented in BackgroundTaskScheduler
    // For now, just reschedule
    this.scheduleNextSync('normal', 'completed_sync');
  }

  /**
   * Add high priority sync
   */
  scheduleHighPrioritySync(reason: string, data?: unknown): void {
    const syncId = `high_${Date.now()}`;
    const scheduledTime = new Date(Date.now() + this.config.highPriorityInterval).toISOString();
    
    const sync: ScheduledSync = {
      id: syncId,
      scheduledTime,
      priority: 'high',
      reason,
      attempts: 0,
      maxAttempts: 3,
      data
    };

    this.scheduledSyncs.set(syncId, sync);
    
    // If this is more urgent than current schedule, reschedule
    if (!this.nextSyncTime || new Date(scheduledTime) < new Date(this.nextSyncTime)) {
      this.scheduleNextSync('high', reason);
    }
  }

  /**
   * Get next scheduled sync time
   */
  getNextSyncTime(): string | null {
    return this.nextSyncTime;
  }

  /**
   * Get current interval
   */
  getCurrentInterval(): number {
    return this.currentInterval;
  }

  /**
   * Get scheduled syncs
   */
  getScheduledSyncs(): Map<string, ScheduledSync> {
    return new Map(this.scheduledSyncs);
  }

  /**
   * Subclass-specific cleanup
   */
  protected cleanupSubclass(): void {
    if (this.scheduleTimer) {
      clearTimeout(this.scheduleTimer);
      this.scheduleTimer = null;
    }
    this.scheduledSyncs.clear();
  }
}
